<script lang="ts">
  /**
   * Le Rang tenu, et celui qu'on vise.
   *
   * Monter en Rang se demande à sa Faction, et la cérémonie revient au meneur
   * (Codex IV p.63). L'application ne la remplace pas : elle dit ce qui est
   * réuni, ce qui manque, et inscrit le résultat une fois que c'est fait.
   */
  import { progression as lireLaProgression, type PersonnageEnregistre } from '../../../rules'

  let {
    personnage,
    promouvoir,
    basculerNarratif,
    accomplirLeSacrement,
  }: {
    personnage: PersonnageEnregistre
    /** Franchit un Rang, une fois tout réuni. */
    promouvoir: (noeud: string) => void
    /** Coche, ou décoche, un Prérequis que la table a tranché. */
    basculerNarratif: (texte: string) => void
    /** Accomplit une cérémonie de Rang, ou revient dessus. */
    accomplirLeSacrement: (sacrement: string) => void
  } = $props()

  const marche = $derived(lireLaProgression(personnage))
</script>

{#if marche.rang_actuel}
  <div class="cartouche">
    <div class="bandeau">Rang</div>
    <div class="corps">
      <p class="rang-tenu">
        <span class="data">Rang {marche.rang_actuel.rang}</span>
        <strong>{marche.rang_actuel.titre}</strong>
        {#if marche.rang_actuel.groupe}
          <span class="voie">{marche.rang_actuel.groupe}</span>
        {/if}
      </p>

      {#if marche.rangs.length === 0}
        <p class="chapo">Le sommet de la Classe : le livre n'écrit rien au-dessus.</p>
      {:else}
        <p class="chapo">
          Monter en Rang se demande à sa Faction, et la cérémonie revient au
          meneur (Codex IV p.63). L'application ne fait que dire ce qui est
          réuni — et l'inscrire une fois que c'est fait.
        </p>
      {/if}

      {#each marche.rangs as r (r.id)}
        <section class="vise" class:ouvert={r.accessible}>
          <h3>
            <span class="data">Rang {r.rang}</span>
            {r.titre}
            {#if r.groupe}<span class="voie">{r.groupe}</span>{/if}
          </h3>

          {#if r.manques.length > 0}
            <ul class="manques">
              {#each r.manques as m (m)}
                <li>{m}</li>
              {/each}
            </ul>
          {/if}

          {#if r.sacrement}
            {@const ceremonie = r.sacrement}
            <div class="ceremonie" class:faite={ceremonie.accompli}>
              <h4>
                {ceremonie.nom}
                <span class="voie">p. {ceremonie.page}</span>
              </h4>
              <p class="detail">{ceremonie.resume}</p>
              <p class="detail">
                <strong class="data">
                  {ceremonie.recompense}
                  {ceremonie.unite === 'pi' ? 'PI' : 'XP'}
                </strong>
                à l'issue de la cérémonie.
              </p>
              <button
                type="button"
                class="pilule"
                onclick={() => accomplirLeSacrement(ceremonie.id)}
              >
                {ceremonie.accompli ? 'Revenir dessus' : 'La cérémonie a eu lieu'}
              </button>
            </div>
          {/if}

          {#each r.narratifs as n (n.texte)}
            <label class="narratif">
              <input
                type="checkbox"
                checked={n.valide}
                onchange={() => basculerNarratif(n.texte)}
              />
              <span>{n.texte}</span>
              <span class="voie">à la table</span>
            </label>
          {/each}

          <ul class="apporte">
            {#if r.competences.length > 0}
              <li>
                <span class="quoi">Compétences avancées</span>
                {r.competences.join(', ')}
                <span class="voie">d'office</span>
              </li>
            {/if}
            {#if r.capacites.length > 0}
              <li>
                <span class="quoi">Capacités</span>
                {r.capacites.join(', ')}
                <span class="voie">à acheter en XP</span>
              </li>
            {/if}
            {#if r.argent !== null}
              <li><span class="quoi">Pécule</span> {r.argent} £</li>
            {/if}
            {#each r.dotations as d (d)}
              <li><span class="quoi">Dotation</span> {d}</li>
            {/each}
          </ul>

          <button
            type="button"
            class="pilule"
            disabled={!r.accessible}
            onclick={() => promouvoir(r.id)}
          >
            Prendre ce Rang
          </button>
        </section>
      {/each}
    </div>
  </div>
{/if}

<style>
  /* Le Rang tenu : la ligne qu'on lit en premier. */
  .rang-tenu {
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    margin: 0 0 var(--pas);
  }
  .rang-tenu strong {
    font-family: var(--titre);
    font-size: 1.05rem;
  }
  /* Un Rang visé : encadré, et plus franc dès qu'il est à portée. */
  .vise {
    border: 1px solid var(--trait);
    border-radius: 3px;
    padding: var(--pas);
    margin-top: var(--pas);
    display: grid;
    gap: calc(var(--pas) / 2);
    opacity: 0.75;
  }
  .vise.ouvert {
    opacity: 1;
    border-color: var(--ok);
  }
  .vise h3 {
    margin: 0;
    font-family: var(--titre);
    font-size: 0.95rem;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
  }
  .vise .pilule {
    justify-self: start;
  }
  .manques {
    margin: 0;
    padding-left: 1.1rem;
    font-size: 0.78rem;
    color: var(--ko);
  }
  /* La cérémonie : ce que le livre en dit, et ce qu'elle rapporte. Encadrée,
     parce qu'elle ne se coche pas — elle se joue. */
  .ceremonie {
    border-left: 2px solid var(--trait);
    padding-left: var(--pas);
    display: grid;
    gap: 2px;
    justify-items: start;
  }
  .ceremonie.faite {
    border-left-color: var(--ok);
  }
  .ceremonie h4 {
    margin: 0;
    font-family: var(--titre);
    font-size: 0.85rem;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
  }
  .ceremonie p {
    margin: 0;
    max-width: 60ch;
  }
  /* Ce que le Rang apporte, annoncé avant qu'on le prenne. */
  .apporte {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 0.78rem;
    display: grid;
    gap: 2px;
  }
  .apporte .quoi {
    font-family: var(--titre);
    font-size: 0.68rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pale);
    margin-right: calc(var(--pas) / 2);
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
</style>
